import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ADMIN_TABLE_FETCH_SIZE, fighterRequestStatus, fighterRequestType, statusBgClasses } from '../constants';
import useDebounce from '../hooks/use-debounce';
import { getDocs, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import { fighterRequestsCollection } from '../utils/firebase';
import { ChevronDown, ChevronUp, Image, X } from 'lucide-react';
import { formatFirestoreTimestamp } from "../utils/common"
import { twMerge } from 'tailwind-merge';
import FighterRequest from './manage-fighter-request';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MANAGE_FIGHTER_REQUEST_PATH } from '../App';

// Colour + label maps so Status / Type render as scannable badges.
const statusBadgeClasses = {
  PENDING: "badge-warning",
  APPROVED: "badge-success",
  REJECTED: "badge-error",
};

const typeBadgeClasses = {
  NEW_REGISTER: "badge-info",
  UPDATE_INFO: "badge-primary",
  REGISTER_EVENT: "badge-accent",
  WEIGHT_CHANGE: "badge-warning",
};

const typeLabels = {
  NEW_REGISTER: "New Register",
  UPDATE_INFO: "Profile Update",
  REGISTER_EVENT: "Event",
  WEIGHT_CHANGE: "Weight",
};

const statusLabel = (s) => (s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase());

// Only these columns are sorted server-side. Status/Type are now driven by the
// filter chips, and limiting sortable columns keeps the required Firestore
// composite indexes to a small, deployable set.
const sortableFields = ["name", "updateAt"];

export default function ManageFighterRequests() {
  const [requests, setRequests] = useState(null)
  const [lastDoc, setLastDoc] = useState(null);
  const [sort, setSort] = useState("updateAt");
  const [order, setOrder] = useState("desc");
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate()

  // Filters (initialised from the URL so the page is deep-linkable,
  // e.g. ?status=PENDING&type=UPDATE_INFO).
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState((searchParams.get("status") || "ALL").toUpperCase());
  const [typeFilter, setTypeFilter] = useState((searchParams.get("type") || "ALL").toUpperCase());

  // Keep the URL in sync with the active filters.
  useEffect(() => {
    const params = {};
    if (statusFilter !== "ALL") params.status = statusFilter;
    if (typeFilter !== "ALL") params.type = typeFilter;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  const size = ADMIN_TABLE_FETCH_SIZE;

  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  // A ref for the IntersectionObserver's sentinel element
  const observerRef = useRef(null);

  // Use a ref for the loading flag to avoid triggering re-creations of fetchData
  const loadingRef = useRef(false);

  const handleTableHeaderClick = (field) => {
    if (!sortableFields.includes(field)) {
      return;
    }
    if (field === sort) {
      setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('asc');
    }
  }

  const buildQuery = useCallback((reset = false) => {
    const constraints = [];

    // Server-side filters (equality). "ALL" means no constraint.
    if (statusFilter !== "ALL") {
      constraints.push(where("status", "==", statusFilter));
    }
    if (typeFilter !== "ALL") {
      constraints.push(where("type", "==", typeFilter));
    }

    if (debouncedSearchTerm.trim() !== "") {
      // A range filter on `name` requires `name` to be the first orderBy.
      constraints.push(where("name", ">=", debouncedSearchTerm));
      constraints.push(where("name", "<=", debouncedSearchTerm + '\uf8ff'));
      constraints.push(orderBy("name", "asc"));
    } else {
      constraints.push(orderBy(sort, order));
    }

    constraints.push(limit(size));

    if (!reset && !!lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    return query(fighterRequestsCollection, ...constraints);
  }, [debouncedSearchTerm, lastDoc, order, size, sort, statusFilter, typeFilter])

  const fetchData = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const q = buildQuery(reset);
      const snapshot = await getDocs(q);
      const fetchedRequests = snapshot?.docs?.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        }
      }) ?? [];

      setRequests((prev) => {
        if (reset) {
          return fetchedRequests;
        } else {
          const arr = [
            ...prev,
            ...fetchedRequests,
          ];
          const seenIds = new Set();
          const uniqueData = arr.filter(item => {
            if (seenIds.has(item.id)) {
              return false;
            }
            seenIds.add(item.id);
            return true;
          });
          return uniqueData;
        }
      });

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      setLastDoc(lastVisible);

      setHasMore(snapshot.docs.length === size);

    } catch (error) {
      console.error('Error fetching fighters data: ', error);
    }
    loadingRef.current = false;
    setLoading(false);
  }, [buildQuery, size]);

  const handleRowClick = (request) => {
    // setSelectedRequest(request);
    navigate(`${MANAGE_FIGHTER_REQUEST_PATH}/${request.id}`)
  }


  useEffect(() => {
    setRequests([]);
    setLastDoc(null);
    setHasMore(true);
    fetchData(true);
  }, [sort, order, debouncedSearchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const firstEntry = entries[0];
      if (firstEntry.isIntersecting && hasMore && !loadingRef.current) {
        fetchData();
      }
    });

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }
    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [hasMore, fetchData]);

  const headers = {
    "name": "Name",
    "authIdentifier": "Identifier",
    "status": "Status",
    "type": "Type",
    "updateAt": "Update At"
  }

  if (!requests) {
    return <div className="page-size flex-center">
      <div className="loading loading-md loading-bars" />
    </div>
  }

  return (
    <>
      <div className='flex flex-row w-full h-full'>
        <div className='flex-center flex-1 items-start w-full overflow-auto h-full'>
          {/* Filter bar — Status / Type stack on mobile, sit in a row from sm: up */}
          <div className='w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6 p-3 border-b border-base-300'>
            <div className='flex flex-row flex-wrap items-center gap-2'>
              <span className='text-xs font-bold uppercase opacity-60 w-12'>Status</span>
              {["ALL", ...Object.values(fighterRequestStatus)].map((s) => (
                <button
                  key={s}
                  className={twMerge('btn btn-xs', statusFilter === s ? 'btn-primary' : 'btn-ghost border border-base-300')}
                  onClick={() => setStatusFilter(s)}
                >
                  {statusLabel(s)}
                </button>
              ))}
            </div>
            <div className='flex flex-row flex-wrap items-center gap-2'>
              <span className='text-xs font-bold uppercase opacity-60 w-12'>Type</span>
              {["ALL", ...Object.values(fighterRequestType)].map((t) => (
                <button
                  key={t}
                  className={twMerge('btn btn-xs', typeFilter === t ? 'btn-primary' : 'btn-ghost border border-base-300')}
                  onClick={() => setTypeFilter(t)}
                >
                  {t === "ALL" ? "All" : (typeLabels[t] ?? t)}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-auto w-full h-full">
            <table className="table min-w-full w-auto">
              {/* head */}
              <thead>
                <tr className='[&>th]:text-center'>
                  <th></th>
                  {
                    Object.keys(headers).map((key, index) => {
                      const header = headers[key];
                      return (
                        <th key={key} className={twMerge(sortableFields.includes(key) && 'hover:bg-base-200 cursor-pointer')} onClick={() => handleTableHeaderClick(key)}>
                          <div className='flex flex-row justify-center items-center gap-2'>
                            {
                              key === "fighterPicture"
                                ? <Image />
                                : <p>{header}</p>
                            }
                            {
                              sort === key ?
                                (
                                  order === "desc"
                                    ? <ChevronUp />
                                    : <ChevronDown />
                                ) : null
                            }
                          </div>
                        </th>
                      )
                    })
                  }
                </tr>
              </thead>
              <tbody>
                {
                  requests.map((request, index) => {
                    const status = request.status?.toUpperCase() ?? "?";
                    const type = request.type?.toUpperCase() ?? "?";
                    return (
                      <tr key={request.id ?? index} className={('hover [&>td]:text-nowrap cursor-pointer')} onClick={() => handleRowClick(request)}>
                        <td className='text-center font-bold'>{index + 1}</td>
                        <td>
                          {request.name}
                        </td>
                        <td>
                          {request.authIdentifier}
                        </td>
                        <td className="text-center">
                          <span className={twMerge('badge badge-sm font-semibold', statusBadgeClasses[status] ?? 'badge-ghost')}>
                            {status}
                          </span>
                        </td>
                        <td className='text-center'>
                          <span className={twMerge('badge badge-sm font-semibold', typeBadgeClasses[type] ?? 'badge-ghost')}>
                            {typeLabels[type] ?? request.type}
                          </span>
                        </td>
                        <td>
                          {formatFirestoreTimestamp(request.updateAt)}
                        </td>
                      </tr>
                    )
                  })
                }
              </tbody>
            </table>
            <div className='flex-center w-full p-2'>
              {/* Display messages based on state */}
              {loading && <div className='loading loading-bars' />}
              {!loading && requests.length === 0 && <p className='flex-center h-36'>NO RESULT</p>}
              {!loading && requests.length > 0 && !hasMore && <p className='opacity-70'>END</p>}
            </div>
            {/* Sentinel element for the Intersection Observer */}
            <div ref={observerRef} style={{ height: '1px' }} />
          </div>
          <div className="divider divider-primary m-0" />
          {/* Bottom Search Bar */}
          <div className='w-full'>
            <div className='flex-center flex-row p-4 pt-2 gap-2'>
              <input type="text" className='input input-bordered w-full' placeholder='Search by Name...' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              {/* <button className='btn btn-primary' onClick={handleCreate}>Create</button> */}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
